import type { GraphData, GraphNode, GraphEdge } from '@packages/shared';

// OPM数据结构定义
interface OPMNode {
  id: string;
  type: string;
  x?: number;
  y?: number;
  properties: {
    name: string;
    description?: string;
    nodeType?: string;
    parentGroupId?: string;
    children?: string[];
    [key: string]: any;
  };
  text?: {
    value: string;
    x: number;
    y: number;
  };
  children?: string[];
}

interface OPMEdge {
  id: string;
  type: string;
  sourceNodeId: string;
  targetNodeId: string;
  text?: string | {
    value: string;
    x: number;
    y: number;
  };
  properties?: {
    edgeType?: string;
    edgeText?: string;
    [key: string]: any;
  };
}

interface OPMData {
  nodes: OPMNode[];
  edges: OPMEdge[];
}

// 节点类型颜色映射
const NODE_TYPE_COLORS: Record<string, string> = {
  rect: '#70E483', // 对象
  circle: '#52c41a', // 属性
  diamond: '#fa8c16', // 关系
  polygon: '#722ed1', // 过程
  'dynamic-group': '#13c2c2', // 分组
  state: '#1890ff', // 状态
};

// 边类型映射配置
const EDGE_TYPE_MAPPING: Record<string, { name: string; color: string }> = {
  'middle-arrow': { name: '聚合关系', color: '#586D8C' },
  'middle-arrow-double-triangle': { name: '展示关系', color: '#586D8C' },
  'middle-arrow-hollow-triangle': { name: '泛化关系', color: '#586D8C' },
  'middle-arrow-triangle-circle': { name: '实例化关系', color: '#586D8C' },
  polyline: { name: '单向关联关系', color: '#666' },
  'bidirectional-arrow': { name: '对等关联关系', color: '#333333' },
  'circle-filled': { name: '实心圆形', color: '#52c41a' },
  'circle-hollow': { name: '空心圆形', color: '#722ed1' },
  'triangle-filled': { name: '实心三角形', color: '#ff4d4f' },
  'triangle-hollow': { name: '空心三角形', color: '#1890ff' },
  'diamond-filled': { name: '实心棱形', color: '#fa8c16' },
  'diamond-hollow': { name: '空心棱形', color: '#eb2f96' },
};

/**
 * 获取节点的颜色
 */
function getNodeColor(node: OPMNode): string {
  // 优先使用节点类型的颜色
  if (NODE_TYPE_COLORS[node.type]) {
    return NODE_TYPE_COLORS[node.type];
  }

  // 如果properties中有节点类型，也尝试映射
  if (node.properties.nodeType && NODE_TYPE_COLORS[node.properties.nodeType]) {
    return NODE_TYPE_COLORS[node.properties.nodeType];
  }

  // 默认颜色
  return '#999999';
}

/**
 * 转换OPM节点为知识图谱节点
 */
function convertNode(opmNode: OPMNode): GraphNode {
  return {
    id: opmNode.id,
    label: opmNode.properties.name || opmNode.text?.value || opmNode.id,
    type: opmNode.type,
    color: getNodeColor(opmNode),
    size: 10,
    shape: 'dot',
    properties: {
      description: opmNode.properties.description,
      originalType: opmNode.type,
      x: opmNode.x,
      y: opmNode.y,
      ...opmNode.properties,
    },
  };
}

/**
 * 转换OPM边为知识图谱边
 */
function convertEdge(opmEdge: OPMEdge, nodes: OPMNode[]): GraphEdge {
  const edgeTypeConfig = EDGE_TYPE_MAPPING[opmEdge.type] || {
    name: opmEdge.type,
    color: '#666',
  };

  // 获取源节点以确定边的颜色
  const sourceNode = nodes.find(n => n.id === opmEdge.sourceNodeId);
  const edgeColor = sourceNode
    ? getNodeColor(sourceNode)
    : edgeTypeConfig.color;

  // 确定边的标签
  let label = edgeTypeConfig.name;

  // 如果有自定义边文字，优先使用自定义文字
  // 边文字可能直接保存在 text 字段，或者保存在 text.value 对象中
  if (opmEdge.text) {
    if (typeof opmEdge.text === 'string') {
      label = opmEdge.text;
    } else if (opmEdge.text?.value) {
      label = opmEdge.text.value;
    }
  } else if (
    (opmEdge.type === 'polyline' || opmEdge.type === 'bidirectional-arrow') &&
    opmEdge.properties?.edgeText
  ) {
    // 向后兼容：如果 text 字段不存在，尝试从 properties.edgeText 获取
    label = opmEdge.properties.edgeText;
  }

  // 确定箭头方向
  let arrows: string = 'to';
  if (opmEdge.type === 'bidirectional-arrow') {
    arrows = 'to,from'; // 双向箭头
  } else if (opmEdge.type === 'middle-arrow' || opmEdge.type.includes('middle-arrow')) {
    arrows = 'to'; // 中间箭头类型，只显示to箭头
  }

  return {
    id: opmEdge.id,
    from: opmEdge.sourceNodeId,
    to: opmEdge.targetNodeId,
    label,
    type: opmEdge.type,
    color: edgeColor,
    width: 0.8,
    arrows: arrows,
    properties: {
      ...opmEdge.properties,
      originalEdgeType: opmEdge.type,
    },
  };
}

/**
 * 创建父子关系虚线边
 */
function createParentChildEdge(parentId: string, childId: string): GraphEdge {
  return {
    id: `parent-child-${parentId}-${childId}`,
    from: parentId,
    to: childId,
    label: '包含',
    type: 'parent-child',
    color: '#999999',
    width: 0.5,
    arrows: 'to',
    dashes: true,
    properties: {
      isVirtual: true,
      relationship: 'parent-child',
    },
  };
}

/**
 * 转换OPM数据为知识图谱数据
 */
export function convertOPMToKnowledgeGraph(opmData: OPMData): GraphData {
  const nodes: GraphNode[] = [];
  const edges: GraphEdge[] = [];
  const processedParentChildPairs = new Set<string>();

  // 转换所有节点
  opmData.nodes.forEach(opmNode => {
    nodes.push(convertNode(opmNode));
  });

  // 转换所有边
  opmData.edges.forEach(opmEdge => {
    edges.push(convertEdge(opmEdge, opmData.nodes));
  });

  // 处理父子关系（状态节点与其父节点之间的关系）
  opmData.nodes.forEach(opmNode => {
    // 处理properties中的parentGroupId
    if (opmNode.properties.parentGroupId) {
      const pairKey = `${opmNode.properties.parentGroupId}-${opmNode.id}`;
      if (!processedParentChildPairs.has(pairKey)) {
        edges.push(
          createParentChildEdge(opmNode.properties.parentGroupId, opmNode.id)
        );
        processedParentChildPairs.add(pairKey);
      }
    }

    // 处理children数组
    if (opmNode.children && opmNode.children.length > 0) {
      opmNode.children.forEach(childId => {
        const pairKey = `${opmNode.id}-${childId}`;
        if (!processedParentChildPairs.has(pairKey)) {
          edges.push(createParentChildEdge(opmNode.id, childId));
          processedParentChildPairs.add(pairKey);
        }
      });
    }
  });

  return {
    nodes,
    edges,
  };
}

/**
 * 异步转换OPM数据为知识图谱数据
 */
export async function convertOPMToKnowledgeGraphAsync(
  opmData: OPMData
): Promise<GraphData> {
  return convertOPMToKnowledgeGraph(opmData);
}
